import React, { useEffect, useRef, useState } from "react";
import notifications from "../../../common/Components/Notifications/Notification";
import { changeSareeStartDate, checkSareeStartDate } from "../../../services/karkhanaApi";
import { useKT } from "../i18n/kt";
import { fmtDay, todayIso } from "../rates/rateModel";
import { StartCheck, startBounds } from "./sareeEditModel";

/**
 * "On the loom since: 19 Oct 2026 · Change". The owner or a manager can move the start earlier or later
 * after work is entered; the server says what is allowed (not after the first entry, not into the loom's
 * previous saree, not in the future) before anything is saved. Every move is logged.
 */
const SareeStartChange = ({
  pieceId,
  start,
  canChange,
  compact = false,
  onChanged,
}: {
  pieceId: string;
  start?: string | null;
  canChange: boolean;
  /** Inside the loom card: the Change button styled like the card's other Change links. */
  compact?: boolean;
  onChanged: () => void;
}) => {
  const { t } = useKT();
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(start || "");
  const [note, setNote] = useState("");
  const [check, setCheck] = useState<StartCheck | null>(null);
  const [checking, setChecking] = useState(false);
  const [busy, setBusy] = useState(false);
  const asked = useRef("");

  useEffect(() => {
    setOpen(false);
    setDate(start || "");
    setCheck(null);
  }, [pieceId, start]);

  // Ask the server about the picked day (and, on opening, about the current one, for the allowed range).
  useEffect(() => {
    if (!open || !date) return;
    const key = `${pieceId}|${date}`;
    asked.current = key;
    setChecking(true);
    checkSareeStartDate(pieceId, date)
      .then((res: any) => {
        if (asked.current !== key) return;
        setCheck(res?.success ? res.data : { ok: false, problem: res?.message || "Could not check the date" });
      })
      .catch(() => asked.current === key && setCheck({ ok: false, problem: "This needs a connection. Try again in a moment." }))
      .finally(() => asked.current === key && setChecking(false));
  }, [open, date, pieceId]);

  const save = async () => {
    setBusy(true);
    try {
      const res: any = await changeSareeStartDate(pieceId, date, note.trim() || undefined);
      if (!res?.success) {
        notifications({ title: t("Not saved"), message: t(res?.message || "Could not save"), type: "danger" });
        return;
      }
      notifications({
        title: t("Saved"),
        message: t("Saree {{n}} is on the loom from {{day}}", { n: res.data?.sareeNumber, day: fmtDay(res.data?.from) }),
        type: "success",
      });
      setOpen(false);
      setNote("");
      onChanged();
    } catch {
      notifications({ title: t("No connection"), message: t("Nothing was saved. Try again."), type: "warning" });
    } finally {
      setBusy(false);
    }
  };

  const bounds = startBounds(check, todayIso());
  const same = !!start && date === start;
  const allowed = !!check?.ok && !same;

  return (
    <div className={`kh-since${compact ? " is-compact" : ""}`}>
      <div className="kh-since__row">
        <span>
          {t("On the loom since")}: <strong>{start ? fmtDay(start) : "—"}</strong>
        </span>
        {canChange && !open ? (
          <button type="button" className={compact ? "kr-who__change" : "btn btn-link btn-sm p-0"} onClick={() => setOpen(true)}>
            {t("Change")}
          </button>
        ) : null}
      </div>
      {open ? (
        <div className="kh-since__edit">
          <label className="kr-label" htmlFor={`kh-since-${pieceId}`}>
            {t("New start date")}
          </label>
          <input
            id={`kh-since-${pieceId}`}
            type="date"
            className="kr-select"
            value={date}
            min={bounds.min || undefined}
            max={bounds.max}
            onChange={(e) => setDate(e.target.value)}
          />
          <p className="kw-muted kh-since__hint">
            {check?.firstEntryDate
              ? t("Work is entered from {{day}}, so the start can be that day or earlier.", { day: fmtDay(check.firstEntryDate) })
              : t("No work entered yet. The start can't be in the future.")}{" "}
            {check?.earliestAllowed
              ? t("Saree {{n}} was on this loom until the day before {{day}}.", { n: check.previousSareeNumber || "", day: fmtDay(check.earliestAllowed) })
              : ""}
          </p>
          {checking ? <p className="kw-muted">{t("Checking…")}</p> : null}
          {!checking && check && !check.ok && !same ? (
            <p className="kh-since__problem" role="alert">
              {t(check.problem || "This date can't be used")}
            </p>
          ) : null}
          {!checking && allowed ? (
            <p className="kh-since__ok">
              {t("Saree moves from {{from}} to {{to}}. Days before work was entered default to its first weaver and helper.", {
                from: fmtDay(check?.currentStart),
                to: fmtDay(date),
              })}
            </p>
          ) : null}
          <label className="kr-label" htmlFor={`kh-since-note-${pieceId}`}>
            {t("Why (optional)")}
          </label>
          <input id={`kh-since-note-${pieceId}`} className="kr-select" value={note} maxLength={300} onChange={(e) => setNote(e.target.value)} />
          <div className="kh-since__actions">
            <button type="button" className="kh-btn" disabled={busy} onClick={() => { setOpen(false); setDate(start || ""); setCheck(null); }}>
              {t("Cancel")}
            </button>
            <button type="button" className="kh-btn is-primary" disabled={busy || checking || !allowed} onClick={save}>
              {busy ? t("Saving…") : t("Save start date")}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default SareeStartChange;
